import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, CreditCard, CheckCircle2, Users, Syringe } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const formatCurrency = (n) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);

const formatDate = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (s || '-');
};

export default function PagosPage() {
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tiposPago, setTiposPago] = useState([]);
  const [forms, setForms] = useState({});
  const [validating, setValidating] = useState(null);

  const fetchPendientes = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/pagos/pendientes`, { withCredentials: true });
      setPendientes(res.data);
    } catch (error) {
      if (!silent) toast.error('Error al cargar pagos pendientes');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendientes();
    const interval = setInterval(() => fetchPendientes(true), 15000);
    return () => clearInterval(interval);
  }, [fetchPendientes]);

  useEffect(() => {
    axios.get(`${API}/catalogs?category=tipo_pago`, { withCredentials: true })
      .then(r => setTiposPago(r.data.map(t => t.value).filter(Boolean)))
      .catch(() => {});
  }, []);

  // Group by atencion_grupo_id (family attentions share one payment)
  const grupos = [];
  const seen = {};
  for (const p of pendientes) {
    const key = p.atencion_grupo_id || p.id;
    if (seen[key] !== undefined) {
      grupos[seen[key]].miembros.push(p);
    } else {
      seen[key] = grupos.length;
      grupos.push({ key, miembros: [p] });
    }
  }

  const getForm = (key, grupo) => forms[key] || {
    tipo_pago: '',
    numero_boleta: '',
    monto: grupo.miembros.length === 1 ? (grupo.miembros[0].monto || 0) : null,
  };

  const setForm = (key, patch, grupo) =>
    setForms(prev => ({ ...prev, [key]: { ...getForm(key, grupo), ...patch } }));

  const validarPago = async (grupo) => {
    const form = getForm(grupo.key, grupo);
    if (!form.tipo_pago) {
      toast.error('Selecciona el tipo de pago');
      return;
    }
    setValidating(grupo.key);
    try {
      const principal = grupo.miembros[0];
      const payload = {
        tipo_pago: form.tipo_pago,
        numero_boleta: form.numero_boleta || '',
        aplicar_a_grupo: grupo.miembros.length > 1,
      };
      if (grupo.miembros.length === 1 && form.monto !== null && form.monto !== undefined) {
        payload.monto = parseFloat(form.monto) || 0;
      }
      const res = await axios.post(`${API}/pagos/${principal.id}/validar`, payload, { withCredentials: true });
      toast.success(res.data.message);
      setForms(prev => {
        const next = { ...prev };
        delete next[grupo.key];
        return next;
      });
      fetchPendientes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al validar el pago');
    } finally {
      setValidating(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="pagos-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-600" />
            Pagos
          </h1>
          <p className="text-slate-500">
            Valida las vacunas y el pago de las atenciones enviadas por enfermería
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
          </div>
        ) : grupos.length === 0 ? (
          <Card>
            <CardContent className="text-center py-16 text-slate-500" data-testid="pagos-empty">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400 opacity-60" />
              <p className="font-medium">No hay pagos pendientes</p>
              <p className="text-sm mt-1">Las nuevas atenciones de enfermería aparecerán aquí automáticamente</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {grupos.map(grupo => {
              const esFamiliar = grupo.miembros.length > 1;
              const total = grupo.miembros.reduce((s, m) => s + (m.monto || 0), 0);
              const form = getForm(grupo.key, grupo);
              return (
                <Card key={grupo.key} className="border-l-4 border-l-amber-400" data-testid={`pago-card-${grupo.key}`}>
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        {esFamiliar ? (
                          <>
                            <Users className="w-4 h-4 text-purple-600" />
                            Atención Familiar ({grupo.miembros.length} pacientes)
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Pago único</Badge>
                          </>
                        ) : (
                          <>
                            <Syringe className="w-4 h-4 text-blue-600" />
                            {grupo.miembros[0].nombre_completo}
                          </>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Creado por {grupo.miembros[0].created_by_name || '-'} · {formatDate(grupo.miembros[0].fecha_vacunacion)}
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="border rounded-lg divide-y">
                      {grupo.miembros.map(m => (
                        <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                          <div>
                            <span className="font-medium">{m.nombre_completo}</span>
                            <span className="text-slate-400 font-mono ml-2">{m.rut || m.documento_consolidado}</span>
                            <div className="text-slate-500 mt-0.5">{m.vacuna}</div>
                          </div>
                          <span className="font-mono font-semibold">{formatCurrency(m.monto)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center p-3 bg-slate-50">
                        <span className="text-sm font-semibold text-slate-600">Total a cobrar</span>
                        <span className="font-bold font-mono text-lg" data-testid={`total-${grupo.key}`}>
                          {formatCurrency(esFamiliar ? total : (form.monto ?? total))}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                      <div>
                        <Label>Tipo de Pago *</Label>
                        <Select
                          value={form.tipo_pago}
                          onValueChange={(v) => setForm(grupo.key, { tipo_pago: v }, grupo)}
                        >
                          <SelectTrigger className="mt-1" data-testid={`tipo-pago-select-${grupo.key}`}>
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposPago.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Nº de Boleta</Label>
                        <Input
                          className="mt-1"
                          value={form.numero_boleta}
                          onChange={(e) => setForm(grupo.key, { numero_boleta: e.target.value }, grupo)}
                          placeholder="Número de boleta"
                          data-testid={`boleta-input-${grupo.key}`}
                        />
                      </div>
                      {!esFamiliar && (
                        <div>
                          <Label>Monto ($)</Label>
                          <Input
                            className="mt-1"
                            type="number"
                            value={form.monto ?? ''}
                            onChange={(e) => setForm(grupo.key, { monto: e.target.value }, grupo)}
                            data-testid={`monto-input-${grupo.key}`}
                          />
                        </div>
                      )}
                      <Button
                        onClick={() => validarPago(grupo)}
                        disabled={validating === grupo.key}
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`validar-btn-${grupo.key}`}
                      >
                        {validating === grupo.key
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <CheckCircle2 className="w-4 h-4" />}
                        Validar Pago
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
